import { I18n } from 'aws-amplify';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Popover, TextField, InputAdornment, List, ListItem, ListItemText,
    CircularProgress, IconButton, Tooltip, Tabs, Tab, Button,
    Select, MenuItem
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { searchPhoneContacts } from '@/service/phoneContactService';
import { normalizeString } from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';
import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';
import Utils from '@/common/utils/utils';
import PhoneCallIcon from '@/component/telephoneAgent/TelephoneIcon/PhoneCallIcon';
import VoiceCallIcon from '@/component/telephoneAgent/TelephoneIcon/VoiceCallIcon';
import './PhoneSearchPopoverV2.css';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { projectInuseDetailAtom, projectListInCorporationByUserIdAtom } from '@/state/atom/projectAtom';
import { voiceStatusAtom, outboundCallAtom } from '@/state/atom/voiceCallAtom';
import { handleAction as twilioHandleAction } from '@/service/twilioService';
import { getB2CUserListInProject } from '../chatThread/handler/TwilioHandler';
import { setupConnectionHandlers } from '@/hooks/useOutboundCall';
import { useSnackbar } from 'notistack';

const { PHONE_SEARCH_TAB, PHONE_SEARCH_PAGE_SIZE, RECIPIENT_TYPE } = TelephoneConstant;

/**
 * OverflowTooltip - Only shows tooltip when text is truncated
 */
function OverflowTooltip({ title, children, ...tooltipProps }) {
    const [isOverflowed, setIsOverflowed] = React.useState(false);
    const ref = React.useRef(null);

    const checkOverflow = React.useCallback(() => {
        const el = ref.current;
        if (el) {
            const textEl = el.querySelector('.MuiListItemText-primary')
                || el.querySelector('.MuiButton-label')
                || el;
            setIsOverflowed(textEl.scrollWidth > textEl.clientWidth);
        }
    }, []);

    React.useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [checkOverflow, title]);

    return (
        <Tooltip title={isOverflowed ? title : ''} placement="right" {...tooltipProps}>
            {React.cloneElement(children, { ref })}
        </Tooltip>
    );
}

/**
 * PhoneSearchPopoverV2 Component
 * Tabbed popover for searching phone contacts, recent calls, and AI training
 *
 * @param {Object} props
 * @param {boolean} props.open - Popover open state
 * @param {Element} props.anchorEl - Anchor element for popover
 * @param {Function} props.onClose - Close popover handler
 * @param {Function} props.onSelectPhone - Phone selection handler
 * @param {Function} props.onMakeCallAiTraining - AI training call handler
 * @param {String} props.projectId - Current project ID
 * @param {String} props.corporationId - Current corporation ID
 * @param {String} props.userId - Current user ID
 */
function PhoneSearchPopoverV2(props) {
    const {
        open,
        anchorEl,
        onClose,
        onSelectPhone,
        onMakeCallAiTraining
    } = props;

    // common userDetail state
    const userDetail = useRecoilValue(userAtom);
    const userId = userDetail?.id;

    // common corporationDetail state
    const corporationDetail = useRecoilValue(corporationAtom);
    const corporationId = corporationDetail?.id;
    
    // common projectInuse state
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const projectId = projectInuseDetail?.id;

    const currentListProjects = useRecoilValue(projectListInCorporationByUserIdAtom);

    // @voice call state
    const setVoiceStatus = useSetRecoilState(voiceStatusAtom);
    const setOutboundCall = useSetRecoilState(outboundCallAtom);
    const { enqueueSnackbar } = useSnackbar();

    const [listB2CUserListInProject, setListB2CUserListInProject] = useState([]);
    
    const isShowTabContactAndHistory = useMemo(() => {
        return Utils.isEnableSettingTelephoneAgent(corporationDetail);
    }, [corporationDetail]);

    const isShowTabTraining = useMemo(() => {
        return projectInuseDetail?.isDesk &&
            corporationDetail?.contractType === Constant.CORPORATION_CONTRACT_TYPE.PREMIUM &&
            Utils.isEnableUsingTelephoneMode(corporationDetail, projectInuseDetail);
    }, [corporationDetail, projectInuseDetail]);

    const [activeTab, setActiveTab] = useState(PHONE_SEARCH_TAB.HISTORY);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [recipientType, setRecipientType] = useState(RECIPIENT_TYPE.PROJECT_IN_ORG);
    const recipientTypeRef = useRef(RECIPIENT_TYPE.PROJECT_IN_ORG);

    // Data states
    const [tabData, setTabData] = useState([]);
    const [isLoadingTabData, setIsLoadingTabData] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Search results
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Pagination for backend pagination (TAB_TRAINING & TAB_CONTACTS)
    const currentPageRef = useRef(0);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMoreTraining, setHasMoreTraining] = useState(false);

    // Pagination: visible count per tab (frontend pagination for HISTORY/CONTACTS)
    const [visibleHistoryCount, setVisibleHistoryCount] = useState(PHONE_SEARCH_PAGE_SIZE);
    const [visibleContactCount, setVisibleContactCount] = useState(PHONE_SEARCH_PAGE_SIZE);

    // Tab order for MUI Tabs index mapping (dynamic based on visible tabs)
    const TAB_ORDER = useMemo(() => {
        const tabs = [];
        if (isShowTabContactAndHistory) {
            tabs.push(PHONE_SEARCH_TAB.HISTORY, PHONE_SEARCH_TAB.CONTACTS);
        }
        if (isShowTabTraining) {
            tabs.push(PHONE_SEARCH_TAB.TRAINING);
        }
        return tabs;
    }, [isShowTabContactAndHistory, isShowTabTraining]);
    const tabIndex = TAB_ORDER.indexOf(activeTab);

    /**
     * Reset all search/pagination state
     */
    const resetSearchState = (isResetSearchKeyword = true) => {
        if (isResetSearchKeyword) {
            setSearchKeyword('');
        }
        setSearchResults([]);
        setHasSearched(false);
        setIsSearching(false);
        setTabData([]);
        setVisibleHistoryCount(PHONE_SEARCH_PAGE_SIZE);
        setVisibleContactCount(PHONE_SEARCH_PAGE_SIZE);
        currentPageRef.current = 0;
        setTotalCount(0);
        setHasMoreTraining(false);
    };

    /**
     * Load data for current tab
     */
    const loadTabData = useCallback(async (tab, keyword = '') => {
        if (!projectId || !corporationId) {
            setTabData([]);
            return;
        }
        setIsLoadingTabData(true);
        try {
            const normalizedKeyword = keyword ? normalizeString(keyword) : '';
            const results = await searchPhoneContacts({
                keyword: normalizedKeyword, corporationId, projectId, userId,
                limit: PHONE_SEARCH_PAGE_SIZE, tabType: tab, recipientType: recipientTypeRef.current
            });
            if (results?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                setShowErrorModal(true);
                setTabData([]);
                return;
            }
            const data = results.data;
            // All tabs return unified format: { items, totalCount, hasMore }
            setTabData(data?.items || []);
            setTotalCount(data?.totalCount || 0);
            setHasMoreTraining(data?.hasMore || false);
            currentPageRef.current = 0;
        } catch (error) {
            setTabData([]);
        } finally {
            setIsLoadingTabData(false);
        }
    }, [projectId, corporationId, userId]);

    /**
     * Load more data (page-based pagination for TAB_TRAINING & TAB_CONTACTS)
     */
    const loadMorePaginated = useCallback(async () => {
        if (!hasMoreTraining) return;
        if (!projectId || !corporationId) return;
        setIsLoadingMore(true);
        try {
            const nextPage = currentPageRef.current + 1;
            const normalizedKeyword = searchKeyword ? normalizeString(searchKeyword) : '';
            const results = await searchPhoneContacts({
                keyword: normalizedKeyword, corporationId, projectId, userId,
                limit: PHONE_SEARCH_PAGE_SIZE, tabType: activeTab,
                page: nextPage, recipientType: recipientTypeRef.current
            });
            if (results?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                setShowErrorModal(true);
                return;
            }
            const data = results.data;
            if (data?.items) {
                if (data.items.length === 0) {
                    setHasMoreTraining(false);
                    return;
                }
                setTabData(prev => [...prev, ...data.items]);
                setHasMoreTraining(data.hasMore || false);
                currentPageRef.current = nextPage;
            }
        } catch (error) {
            console.log('loadMorePaginated error >>>', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [userId, projectId, corporationId, searchKeyword, hasMoreTraining, activeTab]);

    // Reset state when popover opens/closes
    useEffect(() => {
        if (open) {
            resetSearchState();
            const defaultTab = TAB_ORDER[0] || PHONE_SEARCH_TAB.HISTORY;
            setActiveTab(defaultTab);
            recipientTypeRef.current = RECIPIENT_TYPE.PROJECT_IN_ORG;
            setRecipientType(RECIPIENT_TYPE.PROJECT_IN_ORG);
            
            (async () => {
                try {
                    await Promise.all([
                        loadTabData(defaultTab),
                        (async () => {
                            const data = await getB2CUserListInProject(corporationDetail?.id, projectInuseDetail?.id);
                            setListB2CUserListInProject(data);
                        })()
                    ]);
                } catch (err) {
                    console.error('loadTab when open error >>>', err);
                }
            })();
        }
    }, [open]);

    // Reload data when recipientType changes (TAB_CONTACTS only)
    useEffect(() => {
        if (open && activeTab === PHONE_SEARCH_TAB.CONTACTS) {
            loadTabData(PHONE_SEARCH_TAB.CONTACTS, searchKeyword);
        }
    }, [recipientType]); // eslint-disable-line react-hooks/exhaustive-deps



    /**
     * Perform search
     */
    const performSearch = async () => {
        if (!projectId || !corporationId) {
            setSearchResults([]);
            return;
        }

        // Reset pagination state before new search
        setSearchResults([]);
        setHasMoreTraining(false);
        currentPageRef.current = 0;
        setVisibleHistoryCount(PHONE_SEARCH_PAGE_SIZE);
        setVisibleContactCount(PHONE_SEARCH_PAGE_SIZE);

        // TAB_TRAINING & TAB_CONTACTS: use loadTabData (same data flow, supports Load More)
        if (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS) {
            setHasSearched(false);
            loadTabData(activeTab, searchKeyword);
            return;
        }

        // TAB_HISTORY: use searchResults
        setIsSearching(true);
        setHasSearched(true);
        try {
            const normalizedKeyword = normalizeString(searchKeyword);
            const results = await searchPhoneContacts({
                keyword: normalizedKeyword, corporationId, projectId, userId,
                limit: PHONE_SEARCH_PAGE_SIZE, tabType: activeTab, recipientType: recipientTypeRef.current
            });
            if (results?.errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                setShowErrorModal(true);
                setSearchResults([]);
                return;
            }
            const data = results.data;
            setSearchResults(data?.items || []);
        } catch (error) {
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            performSearch();
        }
    };

    const handleSelectPhone = (phoneData) => {
        if (onSelectPhone) onSelectPhone(phoneData);
        handleClose();
    };

    const handleMakeCallAiTraining = (aiOperatorTicket) => {
        if (!aiOperatorTicket) return;
        if (onMakeCallAiTraining) onMakeCallAiTraining(aiOperatorTicket);
        handleClose();
    };

    /**
     * Find B2C user from listB2CUserListInProject by matching phone number
     */
    const findB2CUserByPhone = useCallback((phoneKeyword) => {
        if (!phoneKeyword || !listB2CUserListInProject?.length) return null;
        const cleanedKeyword = Utils.cleanPhone(phoneKeyword);
        if (!cleanedKeyword) return null;
        return listB2CUserListInProject.find((user) => {
            const cleanedTel = Utils.cleanPhone(user?.tel);
            return cleanedTel && cleanedTel === cleanedKeyword;
        }) || null;
    }, [listB2CUserListInProject]);

    const handleDirectCall = () => {
        const cleanedPhone = Utils.cleanPhone(searchKeyword);
        const matchedB2CUser = findB2CUserByPhone(searchKeyword);
        handleSelectPhone({
            phone: cleanedPhone,
            name: matchedB2CUser?.displayName || cleanedPhone,
            type: 'direct',
            userId: matchedB2CUser?.id || null
        });
    };

    const handleVoiceCall = async (phoneData) => {
        if (!phoneData?.projectId && !phoneData?.agentIdentity) return;

        if (!window.twilioDevice) {
            console.error('handleVoiceCall: Twilio device not initialized');
            return;
        }

        try {
            let requestBody = {
                tabType: activeTab,
                recipientType: recipientType,
            }

            if (phoneData.userId) {
                const baseProjectId = currentListProjects?.find(p => p.type === Constant.PROJECT_TYPE.BASE)?.id;
                requestBody = { ...requestBody, calleeUserId: phoneData.userId, baseProjectId: baseProjectId };
            } else if (phoneData.projectId) {
                requestBody = { ...requestBody, calleeProjectId: phoneData.projectId };
            }

            const result = await twilioHandleAction({
                actionType: TelephoneConstant.ACTION.GET_AVAILABLE_AGENTS_FOR_VOICE_CALL,
                corporationInuse: corporationId,
                projectInuse: projectId,
                userId: userId,
                requestBody: JSON.stringify(requestBody),
            });

            if (!result || !result.status) {
                console.error('handleVoiceCall: debug response >>> ', result);
                setShowErrorModal(true);
                return;
            }
            
            // Handle error codes from backend
            if (result?.errorCode) {
                const errorCode = result.errorCode;
                
                // Show Popup 030 for DATA_CHANGED_PLEASE_RELOAD_ERROR_MSG_CODE
                if (errorCode === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR_MSG_CODE) {
                    setShowErrorModal(true);
                    return;
                }
                
                // Show toast notification for other error codes
                const errorMessages = {
                    TEL_INFO_NO_AVAILABLE_RECEIVER: I18n.get('TEL_INFO_NO_AVAILABLE_RECEIVER'),
                    TEL_INFO_CALL_ALREADY_IN_PROGRESS: I18n.get('TEL_INFO_CALL_ALREADY_IN_PROGRESS'),
                    TEL_ERR_TARGET_DELETED: I18n.get('TEL_ERR_TARGET_DELETED'),
                    INTERNAL_ERROR: I18n.get('INTERNAL_ERROR'),
                };
                
                const errorMessage = errorMessages[errorCode] || result?.message || I18n.get('VOICE_CALL_ERROR');
                let variant = 'error';
                if (
                    errorCode === 'TEL_INFO_NO_AVAILABLE_RECEIVER' ||
                    errorCode === 'TEL_INFO_CALL_ALREADY_IN_PROGRESS'
                ) {
                    variant = 'info';
                }
                enqueueSnackbar(errorMessage, { variant: variant, autoHideDuration: 3000 });
                return;
            }

            const agents = result.data?.agents || [];
            if (agents.length === 0) {
                console.log('handleVoiceCall: no online agents');
                enqueueSnackbar(I18n.get('TEL_INFO_NO_AVAILABLE_RECEIVER'), { variant: 'info', autoHideDuration: 3000 });
                return;
            }

            const calleeProjectId = phoneData.projectId || agents[0]?.projectId || '';
            const agentIdentities = agents.map(a => a.identity).join(',');

            const connection = await window?.twilioDevice?.connect({
                params: {
                    To: agentIdentities,
                    callType: TelephoneConstant.CALL_TYPE.VOICE_CALL,
                    callerProjectId: projectId,
                    calleeProjectId: calleeProjectId,
                }
            });
            if (!connection) {
                enqueueSnackbar(I18n.get('VOICE_CALL_ERROR'), { variant: 'error', autoHideDuration: 3000 });
                return;
            }
            window.twilioOutboundConnection = connection;

            // Set outboundCall of state by phoneData
            let outboundDataState = {
                phoneNumber: '',
                customerName: phoneData?.name || '',
                callSid: connection?.parameters?.CallSid || '',
                timestamp: Date.now(),
            };
            const { USER, PROJECT } = Constant.TELEPHONE_SETTINGS.CALL_TYPE; // available target
            const callType = phoneData.callType;
            if (callType === USER) {
                outboundDataState.phoneNumber = phoneData?.userId;
                outboundDataState.image = agents[0]?.image || '/assets/image/icon-user-default.svg';
            } else if (callType === PROJECT) {
                outboundDataState.phoneNumber = phoneData?.projectId;
                outboundDataState.image = '';
            }
            setOutboundCall(outboundDataState);

            setVoiceStatus(Constant.PHONE_AVAILABILITY.CONNECTING);

            setupConnectionHandlers(connection, setOutboundCall, setVoiceStatus, enqueueSnackbar);

            handleClose();
        } catch (error) {
            console.error('handleVoiceCall error:', error);
            enqueueSnackbar(I18n.get('VOICE_CALL_ERROR'), { variant: 'error', autoHideDuration: 3000 });
        }
    };

    const handleClose = () => {
        resetSearchState();
        if (onClose) onClose();
    };

    const handleTabChange = (event, newValue) => {
        const newTab = TAB_ORDER[newValue];
        setActiveTab(newTab);
        const isResetSearchKeyword = false;
        resetSearchState(isResetSearchKeyword);
        loadTabData(newTab);
    };

    const handleRecipientTypeChange = (event) => {
        const newRecipientType = event.target.value;
        recipientTypeRef.current = newRecipientType;
        setRecipientType(newRecipientType);

        // Reset and reload with new recipient type
        const isResetSearchKeyword = false;
        resetSearchState(isResetSearchKeyword);
    };

    // Determine data source (search results vs default tab data)
    const currentData = hasSearched ? searchResults : tabData;

    // Pagination logic
    // TAB_TRAINING: backend pagination (all items in tabData are visible, "Load More" fetches next page)
    // TAB_HISTORY/CONTACTS: frontend pagination (slice from full array)
    const currentVisibleCount = activeTab === PHONE_SEARCH_TAB.HISTORY
        ? visibleHistoryCount
        : activeTab === PHONE_SEARCH_TAB.CONTACTS
            ? visibleContactCount
            : currentData.length; // TRAINING: show all loaded items

    const visibleItems = (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS)
        ? currentData
        : currentData.slice(0, currentVisibleCount);

    const displayTotalCount = (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS)
        ? totalCount
        : currentData.length;

    const hasMore = (activeTab === PHONE_SEARCH_TAB.TRAINING || activeTab === PHONE_SEARCH_TAB.CONTACTS)
        ? hasMoreTraining
        : currentVisibleCount < currentData.length;

    const handleLoadMore = () => {
        if (activeTab === PHONE_SEARCH_TAB.HISTORY) {
            setVisibleHistoryCount((prev) => prev + PHONE_SEARCH_PAGE_SIZE);
        } else {
            // TAB_CONTACTS & TAB_TRAINING: backend pagination
            loadMorePaginated();
        }
    };

    const isLoading = isLoadingTabData;

    // Check if direct call should be shown (no results, valid phone, on History or Contacts tab)
    const showDirectCall = !isLoading && !isSearching &&
        (Utils.isPublicDesk(projectInuseDetail) && Utils.isEnableUsingTelephoneMode(corporationDetail, projectInuseDetail)) &&
        visibleItems.length === 0 &&
        Utils.isValidPhoneNumber(searchKeyword);

    const showNoResults = !isLoading && !isSearching && visibleItems.length === 0 && !showDirectCall;

    return (
        <>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                className="phone-search-popover-v2"
            >
                <div className="psv2-content">
                    {/* Tabs */}
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        className="psv2-tabs"
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        {isShowTabContactAndHistory &&
                            <Tab label={I18n.get('TAB_HISTORY')} className="psv2-tab" />
                        }
                        {isShowTabContactAndHistory &&
                            <Tab label={I18n.get('TAB_CONTACTS')} className="psv2-tab" />
                        }
                        {isShowTabTraining &&
                            <Tab label={I18n.get('TAB_TRAINING')} className="psv2-tab" />
                        }
                    </Tabs>

                    {/* Search Input */}
                    <div className="psv2-search-wrapper">
                        {activeTab === PHONE_SEARCH_TAB.CONTACTS ? (
                            <div className="psv2-search-group">
                                <Select
                                    value={recipientType}
                                    onChange={handleRecipientTypeChange}
                                    variant="outlined"
                                    className="psv2-recipient-select"
                                    MenuProps={{
                                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                                        getContentAnchorEl: null
                                    }}
                                >
                                    <MenuItem value={RECIPIENT_TYPE.PROJECT_IN_ORG}>{I18n.get('RECIPIENT_PROJECT_IN_ORG')}</MenuItem>
                                    {projectInuseDetail?.isDesk &&
                                        <MenuItem value={RECIPIENT_TYPE.USER_IN_ORG}>{I18n.get('RECIPIENT_USER_IN_ORG')}</MenuItem>
                                    }
                                    {(Utils.isPublicDesk(projectInuseDetail) && Utils.isEnableUsingTelephoneMode(corporationDetail, projectInuseDetail)) &&
                                        <MenuItem value={RECIPIENT_TYPE.B2C_CUSTOMER}>{I18n.get('RECIPIENT_PHONE_CALL')}</MenuItem>
                                    }
                                </Select>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    variant="outlined"
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    size="small"
                                    className="psv2-search-field"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <IconButton size="small" onClick={performSearch} disabled={isSearching || !searchKeyword}>
                                                    <SearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        endAdornment: isSearching && (
                                            <InputAdornment position="end">
                                                <CircularProgress size={20} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        ) : (
                            <TextField
                                autoFocus
                                fullWidth
                                variant="outlined"
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                onKeyPress={handleKeyPress}
                                size="small"
                                className="psv2-search-standalone"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton size="small" onClick={performSearch} disabled={isSearching || !searchKeyword}>
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment: isSearching && (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    </div>

                    {/* Count Display */}
                    {!isLoading && !isSearching && displayTotalCount > 0 && (
                        <div className="psv2-count">
                            {I18n.get('DISPLAY_COUNT')}: {visibleItems.length} / {displayTotalCount}
                        </div>
                    )}

                    {/* List Area */}
                    <div className="psv2-list-area">
                        {isLoading && (
                            <div className="psv2-loading">
                                <CircularProgress size={24} />
                                <span className="psv2-loading-text">{I18n.get('SEARCH_LOADING')}</span>
                            </div>
                        )}

                        {!isLoading && !isSearching && visibleItems.length > 0 && (
                            <List className="psv2-list">
                                {activeTab === PHONE_SEARCH_TAB.HISTORY && visibleItems.map((contact, index) => {
                                    const rawDisplayText = contact?.displayText || '';
                                    const displayText = contact?.phone && Utils.isPhoneLikeString(rawDisplayText)
                                        ? Utils.addPlusToPhoneNumber(rawDisplayText.trim())
                                        : rawDisplayText;
                                    const phoneData = {
                                        phone: contact.phone,
                                        name: displayText,
                                        type: contact.type,
                                        userId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.USER ? contact.id : null,
                                        projectId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.PROJECT ? contact.id : (contact.projectId || null),
                                        agentIdentity: contact.agentIdentity || null,
                                        callType: contact.type
                                    };
                                    return (
                                        <OverflowTooltip key={index} title={displayText}>
                                            <ListItem button className="psv2-list-item" onClick={() => {
                                                if (Boolean(contact?.phone)) return handleSelectPhone(phoneData)
                                                if (Boolean(contact?.voice)) return handleVoiceCall(phoneData)
                                                return null;
                                            }}>
                                                <div className="psv2-call-icons">
                                                    {Boolean(contact?.phone) && (
                                                        <div className={`psv2-icon-btn ${contact?.phone && 'bg-yellow'}`}>
                                                            <PhoneCallIcon />
                                                        </div>
                                                    )}
                                                    {Boolean(contact?.voice) && (
                                                        <div className={`psv2-icon-btn ${contact?.voice && 'bg-pink'}`}>
                                                            <VoiceCallIcon />
                                                        </div>
                                                    )}
                                                </div>
                                                <ListItemText primary={displayText} className="psv2-list-text" />
                                            </ListItem>
                                        </OverflowTooltip>
                                    );
                                })}

                                {activeTab === PHONE_SEARCH_TAB.CONTACTS && visibleItems.map((contact, index) => {
                                    const displayText = contact?.displayText || ''
                                    const phoneData = {
                                        phone: contact.phone,
                                        name: displayText,
                                        type: contact.type,
                                        userId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.USER ? contact.id : null,
                                        projectId: contact.type === Constant.TELEPHONE_SETTINGS.CALL_TYPE.PROJECT ? contact.id : (contact.projectId || null),
                                        agentIdentity: contact.agentIdentity || null,
                                        callType: contact.type
                                    };
                                    return (
                                        <OverflowTooltip key={index} title={displayText}>
                                            <ListItem button className="psv2-list-item" onClick={() => {
                                                if (Boolean(contact?.phone)) return handleSelectPhone(phoneData)
                                                if (Boolean(contact?.voice)) return handleVoiceCall(phoneData)
                                                return null;
                                            }}>
                                                <div className="psv2-call-icons">
                                                    {Boolean(contact?.phone) && (
                                                        <div className={`psv2-icon-btn ${contact?.phone && 'bg-yellow'}`}>
                                                            <PhoneCallIcon />
                                                        </div>
                                                    )}
                                                    {Boolean(contact?.voice) && (
                                                        <div className={`psv2-icon-btn ${contact?.voice && 'bg-pink'}`}>
                                                            <VoiceCallIcon />
                                                        </div>
                                                    )}
                                                </div>
                                                <ListItemText primary={displayText} className="psv2-list-text" />
                                            </ListItem>
                                        </OverflowTooltip>
                                    );
                                })}

                                {activeTab === PHONE_SEARCH_TAB.TRAINING && visibleItems.map((ticket) => (
                                    <OverflowTooltip key={ticket?.id} title={ticket?.outline}>
                                        <ListItem button onClick={() => handleMakeCallAiTraining(ticket)} className="psv2-list-item">
                                            <ListItemText primary={ticket?.outline} className="psv2-list-text" />
                                        </ListItem>
                                    </OverflowTooltip>
                                ))}
                            </List>
                        )}

                        {/* Direct Call Button */}
                        {showDirectCall && (
                            <div className="psv2-direct-call">
                                {(() => {
                                    const matchedB2CUser = findB2CUserByPhone(searchKeyword);
                                    const displayLabel = matchedB2CUser?.fullName
                                        ? `${matchedB2CUser.fullName} (${Utils.addPlusToPhoneNumber(Utils.cleanPhone(searchKeyword))})`
                                        : Utils.addPlusToPhoneNumber(Utils.cleanPhone(searchKeyword));
                                    const callToText = I18n.get('CALL_TO');
                                    const fullTitle = `${displayLabel}${callToText}`;
                                    return (
                                        <Tooltip title={fullTitle} placement="top">
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                className="btn-direct-call"
                                                startIcon={!Utils.isEmptyObject(matchedB2CUser) ? <></> : <AddCircleIcon />}
                                                onClick={handleDirectCall}
                                            >
                                                <span className="psv2-direct-call-text">{displayLabel}{callToText}</span>
                                            </Button>
                                        </Tooltip>
                                    );
                                })()}
                            </div>
                        )}

                        {/* No Results */}
                        {showNoResults && (
                            <div className="psv2-no-results">
                                {I18n.get('SEARCH_EMPTY_RESULT')}
                            </div>
                        )}

                        {/* Load More */}
                        {hasMore && !isLoading && !isSearching && (
                            <div className="psv2-load-more">
                                {isLoadingMore ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <button
                                        onClick={handleLoadMore}
                                        className="btn-reset-default psv2-load-more-link"
                                    >
                                        {I18n.get('SEE_MORE')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Popover>

            <DataChangePleaseReloadModal
                isOpen={showErrorModal}
                setIsOpen={setShowErrorModal}
            />
        </>
    );
}

export default PhoneSearchPopoverV2;
